/**
 * Carrier portal app shell.
 *
 * Matches the internal app's visual language (warm-paper bg, Georgia
 * headings, two-strip header pattern) but with different content:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  [logo] <company>                         carrier · sign out     │  ← top strip (64px)
 *   │         <app name>                                                │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * ⭐ Both lines are DATA — `useBranding('CARRIER')`, resolved per app from
 * dbo.vBrandingResolved. They were the literals "Symbia Dock" / "Carrier Portal"
 * until 2026-09-07.
 *
 * No secondary nav strip — the carrier sees a single Dashboard view in
 * Phase 5. No gear menu. The "user info" on the right shows the carrier's
 * email + carrier code instead of the internal user's Entra identity.
 *
 * If the user has no active session, the shell still renders (the login
 * and verify pages don't show the right-side user info).
 */
import { ReactNode } from 'react';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/BrandLogo';
import { useCarrierSession, portalOf, readPortalAccounts, accountLabel } from '@/auth/useCarrierSession';
import { carrierAuthLogout } from '@/api/client';
import { useBranding } from '@/lib/useBranding';

export function CarrierAppShell({ children, portal = 'carrier' }: { children: ReactNode; portal?: 'carrier' | 'client' }) {
    const { session, signOut, setSession } = useCarrierSession();
    /**
     * ⭐⭐ 'CARRIER', NOT 'DOCK'. Migration 077 gave the carrier portal its own
     * AppCode precisely so this header could differ from the scheduler's.
     *
     * ⛔ AND IT IS NOT 'CLIENT'. arch §1K: "a client must never see a carrier's
     * drivers." The client is the shipper whose goods move; the carrier is the
     * trucking company that moves them. Filing this under CLIENT would have
     * entitled every client to the carrier surface the first time
     * dbo.CompanyApp was consulted for real.
     */
    // ⭐ F212: the client portal (the /client pages) is its own app code, 'CLIENT' ("Client Portal") —
    //   the separation above is kept: a client session never gets the carrier surface's drivers.
    const branding = useBranding(portal === 'client' ? 'CLIENT' : 'CARRIER');

    // ⭐ F215: one email, several accounts — switch without another sign-in email
    const accounts = session ? readPortalAccounts() : [];
    const switchTo = (idx: number) => {
        const a = accounts[idx];
        if (!a) return;
        setSession(a);
        window.location.replace(a.kind === 'Client' ? '/client/dashboard' : '/carrier/dashboard');
    };

    const handleSignOut = async () => {
        if (session) {
            // Best-effort tell the server. Server-side it's a no-op today,
            // but we'll add revocation later.
            await carrierAuthLogout(session.sessionToken);
        }
        signOut();
        // Send them to login. Using location.replace avoids back-button
        // returning to the signed-in state.
        window.location.replace(`/${session ? portalOf(session) : portal}/login`);
    };

    return (
        <>
            <header className="app-hdr">
                {/* is-wide (2026-08-17): the dashboard below is uncapped, so the
                    header must be too — otherwise the brand sits in a centred
                    1280px box above a full-width table and reads as a rendering
                    fault. Widen these two together, always. */}
                <div className="app-hdr-inner is-wide">
                    <div className="app-brand">
                        {/**
                          * ⭐⭐ THE CUSTOMER'S LOGO, NOT OURS (2026-09-07).
                          *
                          * !!!! THIS WAS <SymbiaLogoMark> HARDCODED. The Branding
                          *      screen let you set a logo on the CARRIER app, drew
                          *      it in the preview, saved it -- and this shell kept
                          *      painting Symbia's built-in mark. Nothing errored;
                          *      the setting simply moved nothing.
                          *
                          * ** SAME CATEGORY ERROR AS THE TEXT LITERALS on the line
                          *    below, which were "Symbia Dock" / "Carrier Portal"
                          *    until earlier the same day. Caught then for the
                          *    words, missed for the picture.
                          */}
                        <BrandLogo logo={branding.logo} alt={branding.companyName} />
                        <div>
                            {/* ⭐ DATA, NOT LITERALS (2026-09-07). These read
                                "Symbia Dock" / "Carrier Portal" hardcoded, which
                                is why the Branding screen appeared to do nothing
                                here — and "Symbia Dock" on line 1 was the same
                                category error migration 076 cleared out of
                                dbo.Company.ProductName: line 1 is the COMPANY. */}
                            <div className="app-brand-title">{branding.companyName}</div>
                            <div className="app-brand-sub">{branding.appName}</div>
                        </div>
                    </div>

                    {/**
                      * ⛔⛔ THE MIDDLE COLUMN MUST BE OCCUPIED, EVEN WHEN EMPTY.
                      *
                      * `.app-hdr-inner` is `grid-template-columns: 1fr auto 1fr`.
                      * ⚠️ With only TWO children the identity block landed in
                      * column 2 -- the CENTRE -- and right-aligned itself inside
                      * that middle slot, which reads as "floating near the middle"
                      * rather than as a bug. So there is ALWAYS a third child.
                      *
                      * ---- ⭐ AND IT DRAWS THE PAGE TITLE WHEN THERE IS ONE ----
                      *
                      * !!!! THIS WAS AN UNCONDITIONAL SPACER, on the reasoning that
                      *      "the carrier portal has no centre title -- the big
                      *      heading on this page is the CARRIER'S NAME, which is
                      *      data, not chrome."
                      *
                      * ** THAT REASONING IS WITHDRAWN. Pete set a page title on the
                      *    CARRIER app, the Branding screen previewed it on the bar,
                      *    and this shell had nowhere to put it -- so the setting
                      *    stored, previewed and rendered nothing. A field that
                      *    cannot reach a surface is worse than a field that is not
                      *    offered.
                      *
                      * ⚠️ THE SPACER REMAINS THE FALLBACK. Unset, the slot must
                      * still exist or the identity block walks back to the centre.
                      * ⛔ Same shape as brandingPreviewBar's centre column, so the
                      * preview and this header agree by construction.
                      */}
                    {branding.schedulerTitle
                        ? <h1 className="app-page-title" title={branding.schedulerTitle}>{branding.schedulerTitle}</h1>
                        : <span aria-hidden="true" />}

                    {session && (
                        <div className="flex items-center gap-3 justify-self-end">
                            {accounts.length > 1 && (
                                <select
                                    aria-label="Switch account"
                                    className="app-identity rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                                    value={Math.max(0, accounts.findIndex((a) => a.sessionToken === session.sessionToken))}
                                    onChange={(e) => switchTo(Number(e.target.value))}
                                >
                                    {accounts.map((a, i) => <option key={i} value={i}>{accountLabel(a)}</option>)}
                                </select>
                            )}
                            {/* ⭐ app-identity carries --brand-identity-* (083).
                                ⛔ Without it, colouring the header bar leaves
                                this text and the sign-out control in their
                                hardcoded near-black — a branding setting that
                                can hide the way out of the app. */}
                            <div className="app-identity text-right hidden sm:block">
                                <div className="font-medium leading-tight">
                                    {session.displayName || session.email}
                                </div>
                                <div className="text-muted-foreground leading-tight">
                                    {portalOf(session) === 'client' ? (session.clientName || session.clientCode) : session.carrierCode}
                                    {session.displayName && (
                                        <> &middot; {session.email}</>
                                    )}
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" className="app-identity" onClick={handleSignOut} title="Sign out">
                                <LogOut className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                </div>
            </header>

            <main className="flex-1 min-h-0 overflow-y-auto">
                {children}
            </main>
        </>
    );
}
